import { ListModel } from '@/models/list-model';

import * as fb from '@/firebase';
import { AbstractItemModel } from '@/models/items/abstract-item-model';
import { ItemModelUtil } from '@/model-utils/item-model-util';

import { UniqueIdGenerator } from '@/utils/unique-id-generator';
import slugify from 'slugify';
import randomstring from 'randomstring';
import { UserProfileUtil } from './user-profile-util';
import { UserProfile } from '@/models/user-profile';

export class ListModelUtil {
  public static async findListRefFromSlug({
    slug,
    username
  }: {
    slug: string;
    username: string;
  }): Promise<firebase.firestore.DocumentReference> {
    const slugEntry = await fb.userListSlugCollection
      .where('slug', '==', slug)
      .where('username', '==', username)
      .get();
    const listId = slugEntry.docs[0].get('id');
    return fb.listsCollection.doc(listId);
  }

  public static findListRefFromId(
    listId: string
  ): Promise<firebase.firestore.DocumentReference> {
    return Promise.resolve(fb.listsCollection.doc(listId));
  }

  public static deleteList(list: ListModel) {
    // TODO: delete from every other collection too
    const p1 = fb.listsCollection.doc(list.id).delete();
    const p2 = UserProfileUtil.updateNumLists(list.authorId, -1);
    return Promise.all([p1, p2]);
  }

  public static canEditList(list: ListModel, userId: string): boolean {
    return list && list.authorId === userId;
  }

  public static async getListFollowerIds(list: ListModel): Promise<string[]> {
    // TODO: dummy data
    const users = await fb.usersCollection
      .where('username', '>', '')
      .limit(10)
      .get();
    return users.docs.map((doc: firebase.firestore.DocumentSnapshot) => doc.id);
  }

  public static getItemsFromList(
    list: ListModel
  ): Promise<AbstractItemModel[]> {
    const gets = list.itemEntries.map((itemEntry) =>
      fb.itemsCollection.doc(itemEntry.itemId).get()
    );
    return Promise.all(gets).then((docs) => {
      return docs
        .filter((doc) => !!doc.data())
        .map((doc) => ItemModelUtil.itemDocToDomainModel(doc.id, doc.data()));
    });
  }

  public static watchItems(
    list: ListModel,
    cb: (items: AbstractItemModel[]) => void
  ) {
    fb.listsCollection.doc(list.id).onSnapshot((listDoc) => {
      const newList = ListModel.fromFirebaseDoc(listDoc.id, listDoc.data());
      console.log(newList.itemEntries.length);
      this.getItemsFromList(newList).then(cb);
    });

      // fb.itemsCollection.where('listId', '==', list.id).onSnapshot((items) => {
      //   console.log('num items', items.docs.length);
      //   this.hydrateList(list.id).then((newList) => {
      //     console.log(newList.itemEntries.length);
      //     this.getItemsFromList(newList).then(cb);
      //   });
      // });

      // fb.itemsCollection.where('listId', '==', list.id).onSnapshot((items) => {
      //   console.log('num items', items.docs.length);
      //   this.hydrateList(list.id).then((newList) => {
      //     console.log(newList.itemEntries.length);
      //     this.getItemsFromList(newList).then(cb);
      //   });
      // });
  }

  public static listUpdateTransaction(
    listId: string,
    updateCallback: (a: ListModel) => any,
    shouldUpdateModifiedTime: boolean
  ): Promise<any> {
    const listDocRef = fb.listsCollection.doc(listId);
    return fb.firestore
      .runTransaction((transaction) => {
        // This code may get re-run multiple times if there are conflicts.
        return transaction.get(listDocRef).then((listDoc) => {
          if (!listDoc.exists) {
            throw new Error('Document does not exist!');
          }

          const list = ListModel.fromFirebaseDoc(listDoc.id, listDoc.data());
          const updateData = updateCallback(list);

          if (shouldUpdateModifiedTime) {
            updateData.timeLastUpdated = new Date().getTime();
          }
          transaction.update(listDocRef, updateData);
        });
      })
      .catch(function(err) {
        // This will be an "population is too big" error.
        console.error(err);
      });
  }

  public static deleteItemFromList({
    items,
    item,
    listId
  }: {
    items: AbstractItemModel[];
    item: AbstractItemModel;
    listId: string;
  }): Promise<any> {
    return ListModelUtil.listUpdateTransaction(
      listId,
      (curList: ListModel) => {
        return {
          images: curList.images.filter((i) => i !== item.imageURL),
          itemEntries: [
            ...curList.itemEntries
              .filter((e) => e.itemId !== item.id)
              .map((i) => i.toFirebaseDoc())
          ]
        };
      },
      true
    );
  }

  public static watchListsFromUserId(
    userId: string,
    callback: (a: ListModel[]) => void
  ) {
    fb.listsCollection
      .where('authorId', '==', userId)
      .orderBy('timeLastUpdated', 'desc')
      .limit(100)
      .onSnapshot(
        (querySnapshot: firebase.firestore.QuerySnapshot) => {
          callback(ListModelUtil.listsToModels(querySnapshot));
        },
        (error) => {
          console.error('error in watchListsFromUserId', error);
        }
      );
  }

  public static async writeWithDefaultsList(
    title: string,
    user: UserProfile
  ): Promise<ListModel> {
    const list = ListModel.empty();
    list.title = title;
    return ListModelUtil.writeList(list, user);
  }

  public static async updateList({
    oldList,
    list,
    shouldUpdateModifiedTime,
    user
  }: {
    oldList: ListModel;
    list: ListModel;
    shouldUpdateModifiedTime: boolean;
    user: UserProfile;
  }) {
    const titleUpdated = oldList.title !== list.title;
    if (titleUpdated) {
      const possibleSlug = await this.findPossibleSlug({
        title: list.title,
        userId: list.authorId
      });
      list.slug = possibleSlug;
    }

    list.avatar = user.photoURL;
    list.username = user.username;

    if (shouldUpdateModifiedTime) {
      list.setTimeLastUpdated();
    }
    const fbList = list.toFirebaseDoc();

    const listWritePromise = fb.listsCollection.doc(list.id).set(fbList);
    const promisesToWait: Array<Promise<any>> = [listWritePromise];

    if (titleUpdated) {
      fbList.id = list.id;
      const slugWritePromise = fb.userListSlugCollection.add(fbList);
      promisesToWait.push(slugWritePromise);
    }

    if (oldList.isListed !== list.isListed) {
      const numListsUpdatePromise = UserProfileUtil.updateNumLists(
        list.authorId,
        list.isListed ? 1 : -1
      );
      promisesToWait.push(numListsUpdatePromise);
    }

    return Promise.all(promisesToWait).then(() => {
      return list;
    });
  }

  public static async findPossibleSlug({
    userId,
    title
  }: {
    userId: string;
    title: string;
  }): Promise<string> {
    let possibleSlug = slugify(title, { remove: /[^\w\s]/g });
    let isOkay = false;
    let index = 1;

    const maxTries = 10;

    while (!isOkay && index < maxTries) {
      const querySnapshot: firebase.firestore.QuerySnapshot = await fb.listsCollection
        .where('authorId', '==', userId)
        .where('slug', '==', possibleSlug)
        .get();

      if (querySnapshot.empty) {
        isOkay = true;
      } else {
        possibleSlug = slugify(title) + '-' + index.toFixed(0);
        index += 1;
      }
    }

    if (!possibleSlug) {
      possibleSlug = slugify(title) + '-' + randomstring.generate(7);
    }

    return possibleSlug;
  }

  public static async writeList(
    list: ListModel,
    user: UserProfile
  ): Promise<ListModel> {
    const possibleSlug = await this.findPossibleSlug({
      title: list.title,
      userId: user.id
    });
    const listId = UniqueIdGenerator.generate();
    list.slug = possibleSlug;

    list.username = user.username;
    list.authorId = user.id;
    list.avatar = user.photoURL;

    list.id = listId;

    const fbList = list.toFirebaseDoc();

    const listWritePromise = fb.listsCollection.doc(listId).set(fbList);
    fbList.id = listId;
    const slugWritePromise = fb.userListSlugCollection.add(fbList);

    const numListsPromise = Promise.resolve(null);
    if (list.isListed) {
      UserProfileUtil.updateNumLists(list.authorId, 1);
    }

    return Promise.all([listWritePromise, slugWritePromise]).then(() => {
      return list;
    });
  }

  public static getMostRecentLists(limit: number = 10) {
    return fb.listsCollection
      .where('isListed', '==', true)
      .orderBy('timeLastUpdated', 'desc')
      .limit(limit)
      .get()
      .then(this.listsToModels);
  }

  public static getTopListsBySaves(limit: number = 10) {
    return fb.listsCollection
      .where('isListed', '==', true)
      .orderBy('numSaves', 'desc')
      .limit(limit)
      .get()
      .then(this.listsToModels);
  }

  public static getTopListsByUsername({
    username,
    currentUsername,
    limit
  }: {
    username: string;
    currentUsername: string;
    limit: number;
  }): Promise<ListModel[]> {
    let query = fb.listsCollection.where('username', '==', username);

    if (username !== currentUsername) {
      query = query.where('isListed', '==', true);
    }

    return query
      .orderBy('numSaves', 'desc')
      .limit(limit)
      .get()
      .then(this.listsToModels);
  }

  public static listsByUsernameQuery({
    username,
    currentUsername,
    limit
  }: {
    username: string;
    currentUsername: string;
    limit: number;
  }): firebase.firestore.Query {
    let query = fb.listsCollection.where('username', '==', username);

    if (username !== currentUsername) {
      query = query.where('isListed', '==', true);
    }

    if (limit) {
      query = query.limit(limit);
    }

    return query.orderBy('timeLastUpdated', 'desc');
  }

  public static getAllListsByUsername({
    username,
    currentUsername,
    limit
  }: {
    username: string;
    currentUsername: string;
    limit: number;
  }): Promise<ListModel[]> {
    return ListModelUtil.listsByUsernameQuery({
      username,
      currentUsername,
      limit
    })
      .get()
      .then(ListModelUtil.listsToModels);
  }

  public static watchAllListsByUsername(
    {
      username,
      currentUsername,
      limit
    }: {
      username: string;
      currentUsername: string;
      limit: number;
    },
    cb: (lists: ListModel[]) => void
  ): void {
    ListModelUtil.listsByUsernameQuery({
      username,
      currentUsername,
      limit
    }).onSnapshot(
      (querySnapshot) => cb(ListModelUtil.listsToModels(querySnapshot)),
      (error) => {
        console.error('error in watchAllListsByUsername', error);
      }
    );
  }

  public static getHighlightedLists(limit: number = 10) {
    return fb.listsCollection
      .where('isListed', '==', true)
      .where('isHighlighted', '==', true)
      .orderBy('timeLastUpdated', 'desc')
      .limit(limit)
      .get()
      .then(this.listsToModels);
  }

  public static async hydrateLists(listIds: string[]): Promise<ListModel[]> {
    const docPromises = listIds.map((id: string) =>
      fb.listsCollection.doc(id).get()
    );
    const docs = await Promise.all(docPromises);

    const lists: ListModel[] = [];
    docs.forEach((doc: firebase.firestore.QueryDocumentSnapshot) => {
      try {
        const list = ListModel.fromFirebaseDoc(doc.id, doc.data());
        lists.push(list);
      } catch (e) {
        console.error(e);
      }
    });

    return lists;
  }

  public static async hydrateList(listId: string): Promise<ListModel> {
    const doc = await fb.listsCollection.doc(listId).get();

    try {
      return ListModel.fromFirebaseDoc(doc.id, doc.data());
    } catch (e) {
      console.error(e);
    }
  }

  public static listsToModels(
    querySnapshot: firebase.firestore.QuerySnapshot
  ): ListModel[] {
    const lists: ListModel[] = [];
    querySnapshot.forEach((doc: firebase.firestore.QueryDocumentSnapshot) => {
      try {
        const list = ListModel.fromFirebaseDoc(doc.id, doc.data());
        lists.push(list);
      } catch (e) {
        console.error(e);
      }
    });
    return lists;
  }

  public static setIsHighlighted(
    listId: string,
    isHighlighted: boolean
  ): Promise<any> {
    return fb.listsCollection.doc(listId).update({
      isHighlighted
    });
  }
}
